import { useCallback, useEffect, useState } from "react";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import { parseInstant } from "../../utils/dateUtils";
import {
  adminService,
  type StorageBackendHealth,
  type StorageBackendMode,
  type StorageMigrationJob,
  type StorageDomainCacheCheck,
} from "../../features/admin/service";
import type { CollectionStat, SystemHealth, S3BackupStatus, WebsocketConnectionsResponse } from "../../features/admin/types";
import AdminWebSocketHealthPage from "./AdminWebSocketHealthPage";
import AdminRecRedisHealthPage from "./AdminRecRedisHealthPage";
import InactivityNudgePanel from "./InactivityNudgePanel";
import AdminCdnRedisHealthPage from "./AdminCdnRedisHealthPage";
import RefreshButton from "../../components/admin/RefreshButton";

// Order matters: least to most CDN involvement, so the pills read as a scale.
const STORAGE_BACKEND_MODES: { value: StorageBackendMode; label: string }[] = [
  { value: "s3", label: "S3" },
  { value: "bunny_storage", label: "Bunny Storage" },
  { value: "s3_via_bunny_cache", label: "S3 via Bunny cache" },
];

// The three independent circuit breakers reported by GET /api/admin/storage-health.
const STORAGE_HEALTH_BACKENDS: {
  key: "s3" | "bunny_storage" | "bunny_cache";
  label: string;
}[] = [
  { key: "s3", label: "S3" },
  { key: "bunny_storage", label: "Bunny Storage" },
  { key: "bunny_cache", label: "Bunny cache" },
];

const backendLabel = (mode?: string): string =>
  mode === "bunny_storage" ? "Bunny Storage" : mode === "s3_via_bunny_cache" ? "S3 via Bunny cache" : "S3";

const formatRelativeAgo = (ms: number): string => {
  if (ms < 60_000) return "just now";
  const mins = Math.floor(ms / 60_000);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
};

// Explains a Verify-live-delivery result whose resolved mode doesn't match the
// domain's currently configured mode. This is usually NOT a bug: a stored key
// is self-tagged with the backend it actually lives on (see
// app/core/storage_backend.py), so a key written under an old mode keeps
// resolving to that backend until a migration explicitly moves it — the
// config flip alone never touches existing bytes.
const explainStorageModeMismatch = (configuredMode: string, resolvedMode: string): string | null => {
  if (configuredMode === resolvedMode) return null;
  if (resolvedMode === "bunny_storage") {
    return `This particular sampled file is still tagged Bunny Storage from an earlier migration — it hasn't been moved back to ${backendLabel(configuredMode)} yet. Not a bug: use "Migrate existing objects" below to bring leftover Bunny files in line with the current setting.`;
  }
  if (resolvedMode === "s3" && configuredMode === "bunny_storage") {
    return `This particular sampled file hasn't been migrated to Bunny Storage yet — it's still being served from S3 while migration catches up. Use "Migrate existing objects" below, or wait for it to finish.`;
  }
  return `This file resolved through "${backendLabel(resolvedMode)}", not the domain's current "${backendLabel(configuredMode)}" setting — expected right after a routing flip or mid-migration, while things catch up.`;
};

const AdminSettingsPage: React.FC = () => {
  // Each section below fetches independently on mount instead of behind one
  // shared Promise.all — a single slow/hung endpoint (e.g. the Mongo
  // collection-stats scan) used to stick the whole page behind one spinner.
  const [health, setHealth] = useState<SystemHealth | null>(null);
  const [healthLoading, setHealthLoading] = useState(true);
  const [collections, setCollections] = useState<CollectionStat[]>([]);
  const [collectionsLoading, setCollectionsLoading] = useState(true);
  const [wsHealth, setWsHealth] = useState<WebsocketConnectionsResponse | null>(null);
  const [wsHealthLoading, setWsHealthLoading] = useState(true);
  const [wsHealthFailed, setWsHealthFailed] = useState(false);
  const [retention, setRetention] = useState<{
    session_days: number;
    batch_size: number;
    updated_by: string | null;
    updated_at: string | null;
  } | null>(null);
  const [retentionLoading, setRetentionLoading] = useState(true);
  const [retentionForm, setRetentionForm] = useState({ session_days: 0, batch_size: 100 });
  const [retentionSaving, setRetentionSaving] = useState(false);

  type GateRole = "seller" | "buyer";
  type GateFeature = "messaging" | "enquiry" | "deal_creation" | "enquiry_receive" | "ai_chat" | "webinar";
  const GATE_FEATURES: Record<GateRole, GateFeature[]> = {
    seller: ["messaging", "deal_creation", "enquiry", "enquiry_receive", "ai_chat", "webinar"],
    buyer: ["messaging", "enquiry", "ai_chat", "webinar"],
  };
  const GATE_LABELS: Record<GateFeature, string> = {
    messaging: "Messaging (DM threads + sending messages)",
    enquiry: "Enquiries — sending (draft, send, and Deal AI chat's finalize/Yes-No send)",
    deal_creation: "Deal creation",
    enquiry_receive: "Enquiries — receiving (seller inbox)",
    ai_chat: "Deal AI reply chat (Get Details popup only) — finalize/send is gated by Enquiries above instead",
    webinar: "Hosting webinars",
  };
  // Rendered row order — kept separate from GATE_FEATURES (which drives
  // per-role applicability) so the two new gates land grouped with their
  // related row instead of alphabetically.
  const GATE_ROWS: GateFeature[] = ["messaging", "enquiry", "enquiry_receive", "ai_chat", "deal_creation", "webinar"];
  const [gates, setGates] = useState<{
    seller: Partial<Record<GateFeature, boolean>>;
    buyer: Partial<Record<GateFeature, boolean>>;
    // Separate, unrelated gate — NOT tied to is_verified. Whether Get
    // Details AI chat / flyer generation / market research require
    // gst_number + website to be filled in, per role.
    gst_website_required?: { seller?: boolean; buyer?: boolean };
    updated_at: string | null;
  } | null>(null);
  const [gatesLoading, setGatesLoading] = useState(true);
  const [gateSavingKey, setGateSavingKey] = useState<string | null>(null);

  // Separate from Verification Gating above — only governs whether declining
  // (No / X) the deal AI chat confirm dialog still silently sends the
  // enquiry to the seller. Yes is unaffected and always goes through the
  // Enquiries verification gate instead.
  const [declineBehavior, setDeclineBehavior] = useState<{ enabled: boolean; updated_by: string | null; updated_at: string | null } | null>(null);
  const [declineBehaviorLoading, setDeclineBehaviorLoading] = useState(true);
  const [declineBehaviorSaving, setDeclineBehaviorSaving] = useState(false);

  const [pricingSettings, setPricingSettings] = useState<Record<string, { slug: string; name: string; group_slug: string | null; pricing_required: boolean }[]> | null>(null);
  const [pricingLoading, setPricingLoading] = useState(true);
  const [pricingSavingSlug, setPricingSavingSlug] = useState<string | null>(null);

  const [gstVerification, setGstVerification] = useState<{ enabled: boolean; updated_by: string | null; updated_at: string | null } | null>(null);
  const [gstLoading, setGstLoading] = useState(true);
  const [gstSaving, setGstSaving] = useState(false);

  const [verificationEmail, setVerificationEmail] = useState<{ enabled: boolean; updated_by: string | null; updated_at: string | null } | null>(null);
  const [verificationEmailLoading, setVerificationEmailLoading] = useState(true);
  const [verificationEmailSaving, setVerificationEmailSaving] = useState(false);

  const [transactionalEmail, setTransactionalEmail] = useState<{ enabled: boolean; updated_by: string | null; updated_at: string | null } | null>(null);
  const [transactionalEmailLoading, setTransactionalEmailLoading] = useState(true);
  const [transactionalEmailSaving, setTransactionalEmailSaving] = useState(false);

  const [phoneOtp, setPhoneOtp] = useState<{ enabled: boolean; updated_by: string | null; updated_at: string | null } | null>(null);
  const [phoneOtpLoading, setPhoneOtpLoading] = useState(true);
  const [phoneOtpSaving, setPhoneOtpSaving] = useState(false);

  const [uploadCleanup, setUploadCleanup] = useState<{ enabled: boolean; updated_by: string | null; updated_at: string | null } | null>(null);
  const [uploadCleanupLoading, setUploadCleanupLoading] = useState(true);
  const [uploadCleanupSaving, setUploadCleanupSaving] = useState(false);

  const [askFileReading, setAskFileReading] = useState<{ enabled: boolean; updated_by: string | null; updated_at: string | null } | null>(null);
  const [askFileReadingLoading, setAskFileReadingLoading] = useState(true);
  const [askFileReadingSaving, setAskFileReadingSaving] = useState(false);

  const [s3Backup, setS3Backup] = useState<S3BackupStatus | null>(null);
  const [s3BackupLoading, setS3BackupLoading] = useState(true);
  const [s3BackupTriggering, setS3BackupTriggering] = useState(false);

  const [storageRouting, setStorageRouting] = useState<{
    default_backend: string;
    domains: Record<string, string>;
    domain_groups: Record<string, string>;
    domain_labels: Record<string, string>;
    domain_updated_at: Record<string, string>;
    updated_by: string | null;
    updated_at: string | null;
  } | null>(null);
  const [storageRoutingLoading, setStorageRoutingLoading] = useState(true);
  const [storageRoutingSavingDomain, setStorageRoutingSavingDomain] = useState<string | null>(null);

  const [storageHealth, setStorageHealth] = useState<{
    s3: StorageBackendHealth;
    bunny_storage: StorageBackendHealth;
    bunny_cache: StorageBackendHealth;
  } | null>(null);

  // Latest migration job per domain, keyed by domain so several domains can be
  // migrating at once with fully independent buttons and progress panels —
  // which is the point of the backend queue. The counters come straight from
  // the job document the worker checkpoints after each 500-document window.
  const [migrations, setMigrations] = useState<Record<string, StorageMigrationJob>>({});
  // Failure of the enqueue REQUEST itself (e.g. a 400 for a domain that isn't
  // routed to bunny_storage). A failure of the job is job.error instead.
  const [migrationFailure, setMigrationFailure] = useState<Record<string, string>>({});
  const [migrationStarting, setMigrationStarting] = useState<Record<string, boolean>>({});
  const MIGRATION_POLL_MS = 2000;

  // Result of the one-key live-delivery probe per domain (GET .../cache-check) —
  // shows what a real browser would actually load right now, independent of
  // what's stored. The only way to see an "s3_via_bunny_cache" domain is truly
  // being served through Bunny, since its stored keys never change at all.
  const [cacheCheck, setCacheCheck] = useState<Record<string, StorageDomainCacheCheck>>({});
  const [cacheChecking, setCacheChecking] = useState<Record<string, boolean>>({});

  const [inventoryDomain, setInventoryDomain] = useState<string>("deals");
  const [inventory, setInventory] = useState<Awaited<ReturnType<typeof adminService.getStorageInventory>> | null>(null);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [inventorySkip, setInventorySkip] = useState(0);
  const [inventorySellerName, setInventorySellerName] = useState("");
  const [inventoryOwners, setInventoryOwners] = useState<{ id: string; name: string; email: string | null }[]>([]);
  const [inventoryLastSynced, setInventoryLastSynced] = useState<Date | null>(null);
  const INVENTORY_PAGE_SIZE = 5;

  const fetchAllSettings = useCallback(() => {
    return Promise.all([
      adminService.getSystemHealth()
        .then(setHealth)
        .catch((err) => console.error(err))
        .finally(() => setHealthLoading(false)),

      adminService.getCollectionStats()
        .then(setCollections)
        .catch((err) => console.error(err))
        .finally(() => setCollectionsLoading(false)),

      adminService.getWebsocketConnections()
        .then((res) => {
          setWsHealth(res);
          setWsHealthFailed(false);
        })
        .catch((err) => {
          console.error(err);
          setWsHealth(null);
          setWsHealthFailed(true);
        })
        .finally(() => setWsHealthLoading(false)),

      adminService.getRagRetention()
        .then((ret) => {
          setRetention(ret);
          setRetentionForm({ session_days: ret.session_days, batch_size: ret.batch_size ?? 100 });
        })
        .catch((err) => console.error(err))
        .finally(() => setRetentionLoading(false)),

      adminService.getVerificationGates()
        .then(setGates)
        .catch((err) => console.error(err))
        .finally(() => setGatesLoading(false)),

      adminService.getSilentEnquiryOnDecline()
        .then(setDeclineBehavior)
        .catch((err) => console.error(err))
        .finally(() => setDeclineBehaviorLoading(false)),

      adminService.getPricingSettings()
        .then((res) => setPricingSettings(res.groups))
        .catch((err) => console.error(err))
        .finally(() => setPricingLoading(false)),

      adminService.getGstVerification()
        .then(setGstVerification)
        .catch((err) => console.error(err))
        .finally(() => setGstLoading(false)),

      adminService.getVerificationEmail()
        .then(setVerificationEmail)
        .catch((err) => console.error(err))
        .finally(() => setVerificationEmailLoading(false)),

      adminService.getTransactionalEmail()
        .then(setTransactionalEmail)
        .catch((err) => console.error(err))
        .finally(() => setTransactionalEmailLoading(false)),

      adminService.getPhoneOtp()
        .then(setPhoneOtp)
        .catch((err) => console.error(err))
        .finally(() => setPhoneOtpLoading(false)),

      adminService.getUploadCleanup()
        .then(setUploadCleanup)
        .catch((err) => console.error(err))
        .finally(() => setUploadCleanupLoading(false)),

      adminService.getAskFileReading()
        .then(setAskFileReading)
        .catch((err) => console.error(err))
        .finally(() => setAskFileReadingLoading(false)),

      adminService.getS3BackupStatus()
        .then(setS3Backup)
        .catch((err) => console.error(err))
        .finally(() => setS3BackupLoading(false)),

      adminService.getStorageRouting()
        .then(setStorageRouting)
        .catch(() => {})
        .finally(() => setStorageRoutingLoading(false)),

      adminService.getStorageHealth()
        .then(setStorageHealth)
        .catch(() => setStorageHealth(null)),

      // Migrations outlive this page now, so pick up whatever is already in
      // flight (or last finished) per domain instead of showing nothing until
      // someone clicks again. Jobs come back newest-first, so the first entry
      // seen for a domain is the one to keep.
      adminService.listStorageMigrationJobs()
        .then(({ jobs }) => {
          const latest: Record<string, StorageMigrationJob> = {};
          jobs.forEach((job) => {
            if (!latest[job.domain]) latest[job.domain] = job;
          });
          setMigrations(latest);
        })
        .catch(() => {}),
    ]).then(() => {});
  }, []);

  useEffect(() => {
    fetchAllSettings();
  }, [fetchAllSettings]);

  // Sync runs server-side in a background task -- poll while "running" so
  // the tab reflects completion without a manual refresh.
  useEffect(() => {
    if (s3Backup?.state !== "running") return;
    const interval = setInterval(() => {
      adminService.getS3BackupStatus().then(setS3Backup).catch((err) => console.error(err));
    }, 5000);
    return () => clearInterval(interval);
  }, [s3Backup?.state]);

  const triggerS3Backup = async () => {
    setS3BackupTriggering(true);
    try {
      const { status: newStatus } = await adminService.triggerS3Backup();
      setS3Backup(newStatus);
    } catch (err: any) {
      // 409 = already running -- backend returns the current status in the error body
      const existing = err?.response?.data?.detail?.status;
      if (existing) setS3Backup(existing);
      else console.error(err);
    } finally {
      setS3BackupTriggering(false);
    }
  };

  const toggleGstVerification = async () => {
    if (!gstVerification) return;
    setGstSaving(true);
    try {
      const updated = await adminService.updateGstVerification(!gstVerification.enabled);
      setGstVerification(updated);
    } catch (err) {
      console.error(err);
    } finally {
      setGstSaving(false);
    }
  };

  const toggleVerificationEmail = async () => {
    if (!verificationEmail) return;
    setVerificationEmailSaving(true);
    try {
      const updated = await adminService.updateVerificationEmail(!verificationEmail.enabled);
      setVerificationEmail(updated);
    } catch (err) {
      console.error(err);
    } finally {
      setVerificationEmailSaving(false);
    }
  };

  const toggleTransactionalEmail = async () => {
    if (!transactionalEmail) return;
    setTransactionalEmailSaving(true);
    try {
      const updated = await adminService.updateTransactionalEmail(!transactionalEmail.enabled);
      setTransactionalEmail(updated);
    } catch (err) {
      console.error(err);
    } finally {
      setTransactionalEmailSaving(false);
    }
  };

  const loadInventory = async (domain: string, skip: number, sellerName?: string) => {
    setInventoryLoading(true);
    try {
      const data = await adminService.getStorageInventory(domain, {
        skip,
        limit: INVENTORY_PAGE_SIZE,
        seller_name: sellerName || undefined,
      });
      setInventory(data);
      setInventoryLastSynced(new Date());
    } catch {
      setInventory(null);
    } finally {
      setInventoryLoading(false);
    }
  };

  const setStorageDomainBackend = async (domain: string, backend: StorageBackendMode) => {
    setStorageRoutingSavingDomain(domain);
    try {
      const updated = await adminService.updateStorageRoutingDomain(domain, backend);
      // Merge, don't replace: the PUT response doesn't repeat the registry
      // metadata (domain_groups/domain_labels) the last GET already loaded —
      // replacing wholesale would blank out the grouping for every row.
      setStorageRouting((prev) => (prev ? { ...prev, ...updated } : null));
      // Flipping TO Bunny Storage enqueues that domain's migration server-side,
      // so adopt the job the flip returned and let the poll effect pick it up —
      // the admin sees progress immediately instead of having to also find and
      // click "Migrate existing objects".
      if (updated.migration_job) {
        setMigrations((prev) => ({ ...prev, [domain]: updated.migration_job! }));
      }
    } finally {
      setStorageRoutingSavingDomain(null);
    }
  };

  // Queues the migration and hands off to the poll effect below. The click no
  // longer does the work: the backend worker walks the domain's collections and
  // checkpoints its progress, so closing this page doesn't stop the migration
  // and reopening it picks the job back up.
  const startStorageDomainMigration = async (domain: string) => {
    setMigrationStarting((prev) => ({ ...prev, [domain]: true }));
    setMigrationFailure((prev) => {
      const next = { ...prev };
      delete next[domain];
      return next;
    });
    try {
      const { job } = await adminService.enqueueStorageDomainMigration(domain);
      setMigrations((prev) => ({ ...prev, [domain]: job }));
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } }).response?.data?.detail;
      setMigrationFailure((prev) => ({
        ...prev,
        [domain]: detail || (err as Error).message || "Could not queue the migration",
      }));
    } finally {
      setMigrationStarting((prev) => ({ ...prev, [domain]: false }));
    }
  };

  // Fetches one real key for this domain, resolves it through the live read
  // path, and fetches one byte from the result — a one-click, human-readable
  // answer to "is this domain's mode actually working", independent of what's
  // stored (see checkStorageDomainCacheDelivery's doc comment).
  const runStorageDomainCacheCheck = async (domain: string) => {
    setCacheChecking((prev) => ({ ...prev, [domain]: true }));
    try {
      const result = await adminService.checkStorageDomainCacheDelivery(domain);
      setCacheCheck((prev) => ({ ...prev, [domain]: result }));
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } }).response?.data?.detail;
      setCacheCheck((prev) => ({
        ...prev,
        [domain]: {
          domain,
          sample_key: null,
          reachable: false,
          error: detail || (err as Error).message || "Could not run the check",
        },
      }));
    } finally {
      setCacheChecking((prev) => ({ ...prev, [domain]: false }));
    }
  };

  const activeMigrationDomains = Object.values(migrations)
    .filter((job) => job.status === "queued" || job.status === "in_progress")
    .map((job) => job.domain)
    .sort();
  // Joined into a string so the effect below re-subscribes only when the SET of
  // in-flight domains changes, not on every progress tick (which would clear
  // and re-arm the interval two times a second).
  const activeMigrationKey = activeMigrationDomains.join(",");

  // One interval, one independent request per in-flight domain — a slow poll
  // for one domain can't hold up another's progress. Stops as soon as every
  // job reaches completed/failed, and on unmount via the cleanup.
  useEffect(() => {
    if (!activeMigrationKey) return;
    const domains = activeMigrationKey.split(",");
    const timer = window.setInterval(() => {
      domains.forEach((domain) => {
        adminService.getStorageDomainMigrationStatus(domain)
          .then(({ job }) => {
            if (job) setMigrations((prev) => ({ ...prev, [domain]: job }));
          })
          .catch((err) => console.error(err));
      });
    }, MIGRATION_POLL_MS);
    return () => window.clearInterval(timer);
  }, [activeMigrationKey]);

  const togglePhoneOtp = async () => {
    if (!phoneOtp) return;
    setPhoneOtpSaving(true);
    try {
      const updated = await adminService.updatePhoneOtp(!phoneOtp.enabled);
      setPhoneOtp(updated);
    } catch (err) {
      console.error(err);
    } finally {
      setPhoneOtpSaving(false);
    }
  };

  const toggleAskFileReading = async () => {
    if (!askFileReading) return;
    setAskFileReadingSaving(true);
    try {
      const updated = await adminService.updateAskFileReading(!askFileReading.enabled);
      setAskFileReading(updated);
    } catch (err) {
      console.error(err);
    } finally {
      setAskFileReadingSaving(false);
    }
  };

  const toggleUploadCleanup = async () => {
    if (!uploadCleanup) return;
    setUploadCleanupSaving(true);
    try {
      const updated = await adminService.updateUploadCleanup(!uploadCleanup.enabled);
      setUploadCleanup(updated);
    } catch (err) {
      console.error(err);
    } finally {
      setUploadCleanupSaving(false);
    }
  };

  const toggleDeclineBehavior = async () => {
    if (!declineBehavior) return;
    setDeclineBehaviorSaving(true);
    try {
      const updated = await adminService.updateSilentEnquiryOnDecline(!declineBehavior.enabled);
      setDeclineBehavior(updated);
    } catch (err) {
      console.error(err);
    } finally {
      setDeclineBehaviorSaving(false);
    }
  };

  const toggleGate = async (role: GateRole, feature: GateFeature) => {
    if (!gates) return;
    const key = `${role}.${feature}`;
    const nextValue = !gates[role]?.[feature];
    setGateSavingKey(key);
    try {
      const updated = await adminService.updateVerificationGates({ [role]: { [feature]: nextValue } } as any);
      setGates(updated);
    } catch (err) {
      console.error(err);
    } finally {
      setGateSavingKey(null);
    }
  };

  const toggleGstWebsiteGate = async (role: GateRole) => {
    if (!gates) return;
    const key = `gst_website_required.${role}`;
    const nextValue = !gates.gst_website_required?.[role];
    setGateSavingKey(key);
    try {
      const updated = await adminService.updateVerificationGates({ gst_website_required: { [role]: nextValue } } as any);
      setGates(updated);
    } catch (err) {
      console.error(err);
    } finally {
      setGateSavingKey(null);
    }
  };

  const togglePricingRequired = async (slug: string, current: boolean) => {
    if (!pricingSettings) return;
    const nextValue = !current;
    const previous = pricingSettings;
    setPricingSavingSlug(slug);
    setPricingSettings((prev) => {
      if (!prev) return prev;
      const next: typeof prev = {};
      for (const [group, cats] of Object.entries(prev)) {
        next[group] = cats.map((cat) => (cat.slug === slug ? { ...cat, pricing_required: nextValue } : cat));
      }
      return next;
    });
    try {
      await adminService.updatePricingRequired(slug, nextValue);
    } catch (err) {
      console.error(err);
      setPricingSettings(previous);
    } finally {
      setPricingSavingSlug(null);
    }
  };

  const saveRetention = async () => {
    setRetentionSaving(true);
    try {
      const data = await adminService.updateRagRetention(retentionForm);
      setRetention({ session_days: retentionForm.session_days, batch_size: retentionForm.batch_size, updated_by: data.updated_by, updated_at: data.updated_at ?? new Date().toISOString() });
    } catch (e) {
      console.error(e);
    } finally {
      setRetentionSaving(false);
    }
  };

  const totalDocs = collections.reduce((acc, c) => acc + c.documents, 0);
  const sectionSpinner = (
    <div className="flex items-center justify-center py-10">
      <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  type TabKey = "health" | "gating" | "pricing" | "email" | "retention" | "inactivityNudge" | "s3backup" | "storage" | "websocket" | "recRedisHealth" | "cdnRedisHealth";
  const TABS: { key: TabKey; label: string }[] = [
    { key: "health", label: "Health & Database" },
    { key: "gating", label: "Verification Gating" },
    { key: "pricing", label: "Pricing" },
    { key: "email", label: "Email Notifications" },
    { key: "retention", label: "Data Retention" },
    { key: "inactivityNudge", label: "WhatsApp Nudges" },
    { key: "s3backup", label: "S3 Backup" },
    { key: "storage", label: "Storage Routing (CDN)" },
    { key: "websocket", label: "WebSocket Health" },
    { key: "recRedisHealth", label: "Rec Redis Health" },
    { key: "cdnRedisHealth", label: "CDN Redis Health" },
  ];
  const [activeTab, setActiveTab] = useState<TabKey>("health");

  useEffect(() => {
    if (activeTab === "storage") {
      setInventorySkip(0);
      setInventorySellerName("");
      loadInventory(inventoryDomain, 0);
      adminService.getStorageInventoryOwners(inventoryDomain)
        .then((d) => setInventoryOwners(d.owners))
        .catch(() => setInventoryOwners([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, inventoryDomain]);

  // Groups are pure display metadata from the domain registry (see
  // app.core.storage_domains' "group" field) — never consulted by any
  // routing/migration decision, just how this panel is organized so 13+
  // domains don't read as one flat wall of identical cards.
  const STORAGE_GROUP_ORDER = ["Deals", "People", "Content", "Messaging", "Admin", "Other"];
  const storageDomainGroups: Array<[string, Array<[string, string]>]> = (() => {
    if (!storageRouting) return [];
    const byGroup: Record<string, Array<[string, string]>> = {};
    for (const [domain, backend] of Object.entries(storageRouting.domains)) {
      const group = storageRouting.domain_groups[domain] || "Other";
      (byGroup[group] ||= []).push([domain, backend]);
    }
    for (const list of Object.values(byGroup)) list.sort(([a], [b]) => a.localeCompare(b));
    return Object.entries(byGroup).sort(([a], [b]) => {
      const ia = STORAGE_GROUP_ORDER.indexOf(a);
      const ib = STORAGE_GROUP_ORDER.indexOf(b);
      return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
    });
  })();

  return (
    <div>
      <AdminPageHeader
        title="Settings & System"
        subtitle="System health, verification rules, notifications, and retention."
        actions={<RefreshButton onRefresh={fetchAllSettings} />}
      />

      <div className="flex flex-wrap gap-1 border-b border-gray-800 mb-8">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === tab.key
                ? "border-indigo-500 text-white"
                : "border-transparent text-gray-500 hover:text-gray-300"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "health" && (
      <>
      {/* Health Status */}
      {healthLoading ? sectionSpinner : (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
          <p className="text-sm text-gray-500 mb-1">System Status</p>
          <div className="flex items-center gap-2">
            <span className={`w-2.5 h-2.5 rounded-full ${health?.status === "healthy" ? "bg-green-400" : "bg-amber-400"}`} />
            <span className={`text-lg font-semibold ${health?.status === "healthy" ? "text-green-400" : "text-amber-400"}`}>
              {health?.status?.toUpperCase() || "UNKNOWN"}
            </span>
          </div>
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
          <p className="text-sm text-gray-500 mb-1">Database</p>
          <div className="flex items-center gap-2">
            <span className={`w-2.5 h-2.5 rounded-full ${health?.database === "connected" ? "bg-green-400" : "bg-red-400"}`} />
            <span className="text-lg font-semibold text-white">{health?.database || "unknown"}</span>
          </div>
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
          <p className="text-sm text-gray-500 mb-1">Redis</p>
          <div className="flex items-center gap-2">
            <span className={`w-2.5 h-2.5 rounded-full ${health?.redis === "connected" ? "bg-green-400" : "bg-red-400"}`} />
            <span className="text-lg font-semibold text-white">{health?.redis || "unknown"}</span>
          </div>
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
          <p className="text-sm text-gray-500 mb-1">Version</p>
          <span className="text-lg font-semibold text-white">{health?.version || "—"}</span>
        </div>
        <button
          type="button"
          onClick={() => setActiveTab("websocket")}
          className="text-left bg-gray-900 border border-gray-800 rounded-xl p-5 hover:border-gray-600 transition-colors"
        >
          <p className="text-sm text-gray-500 mb-1">WebSocket</p>
          {wsHealthLoading ? (
            <span className="text-lg font-semibold text-gray-500">…</span>
          ) : wsHealthFailed ? (
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
              <span className="text-lg font-semibold text-red-400">FAILED</span>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <span className={`w-2.5 h-2.5 rounded-full ${wsHealth?.redis_enabled ? "bg-green-400" : "bg-amber-400"}`} />
              <span className="text-lg font-semibold text-white">
                {wsHealth?.total_connections ?? 0} live
              </span>
            </div>
          )}
          {!wsHealthLoading && (
            <p className="text-xs text-gray-500 mt-1">
              {wsHealthFailed
                ? "Could not reach websocket-connections endpoint"
                : `Redis clients ${wsHealth?.redis_clients_connected ?? "—"}/${wsHealth?.redis_clients_max ?? "—"}`}
            </p>
          )}
        </button>
      </div>
      )}

      {/* Database Collections */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-800 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">Database Collections</h2>
          {!collectionsLoading && (
            <span className="text-sm text-gray-500">{collections.length} collections, {totalDocs.toLocaleString()} documents</span>
          )}
        </div>
        {collectionsLoading ? sectionSpinner : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800">
                <th className="text-left py-3 px-6 text-gray-500 font-medium">Collection</th>
                <th className="text-right py-3 px-6 text-gray-500 font-medium">Documents</th>
                <th className="text-right py-3 px-6 text-gray-500 font-medium">% of Total</th>
              </tr>
            </thead>
            <tbody>
              {collections.map((c) => (
                <tr key={c.collection} className="border-b border-gray-800/50 hover:bg-gray-800/30 transition-colors">
                  <td className="py-3 px-6">
                    <code className="text-indigo-400 text-xs bg-indigo-500/10 px-2 py-1 rounded">{c.collection}</code>
                  </td>
                  <td className="py-3 px-6 text-right text-white font-medium">{c.documents.toLocaleString()}</td>
                  <td className="py-3 px-6 text-right text-gray-500">
                    {totalDocs > 0 ? ((c.documents / totalDocs) * 100).toFixed(1) : 0}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
      </div>
      </>
      )}

      {activeTab === "gating" && (
      <>
      {/* Verification Gating */}
      <div>
        <h2 className="text-lg font-semibold text-white mb-1">Verification Gating</h2>
        <p className="text-sm text-gray-500 mb-4">
          Control which features are blocked for a seller/buyer whose account isn't yet admin-verified.
          Turning a toggle off lets unverified users use that feature immediately; turning it on blocks
          them until an admin verifies their account. Each toggle enforces against whichever role is
          actually calling — e.g. Deal AI chat / enquiries are reachable by a seller enquiring on another
          seller's deal, not just buyers, and the Seller column is checked for that caller too.
        </p>
        {gatesLoading ? sectionSpinner : (
        <>
        <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden max-w-2xl">
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800">
                <th className="text-left py-3 px-6 text-gray-500 font-medium">Feature</th>
                <th className="text-center py-3 px-6 text-gray-500 font-medium">Seller</th>
                <th className="text-center py-3 px-6 text-gray-500 font-medium">Buyer</th>
              </tr>
            </thead>
            <tbody>
              {GATE_ROWS.map((feature) => (
                <tr key={feature} className="border-b border-gray-800/50">
                  <td className="py-3 px-6 text-gray-300">{GATE_LABELS[feature]}</td>
                  {(["seller", "buyer"] as GateRole[]).map((role) => {
                    const applicable = GATE_FEATURES[role].includes(feature);
                    const key = `${role}.${feature}`;
                    const enabled = !!gates?.[role]?.[feature];
                    return (
                      <td key={role} className="py-3 px-6 text-center">
                        {!applicable ? (
                          <span className="text-gray-600 text-xs">N/A</span>
                        ) : (
                          <button
                            onClick={() => toggleGate(role, feature)}
                            disabled={gateSavingKey === key}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 ${
                              enabled ? "bg-indigo-600" : "bg-gray-700"
                            }`}
                            aria-label={`${enabled ? "Disable" : "Enable"} verification requirement for ${role} ${feature}`}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                enabled ? "translate-x-6" : "translate-x-1"
                              }`}
                            />
                          </button>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
        {gates?.updated_at && (
          <p className="text-xs text-gray-500 mt-2">Last updated: {(parseInstant(gates.updated_at) ?? new Date(gates.updated_at)).toLocaleString()}</p>
        )}
        </>
        )}
      </div>

      {/* GST / website completeness gate — separate from Verification Gating
          above: NOT tied to is_verified, just whether the field data exists. */}
      <div className="mt-8">
        <h2 className="text-lg font-semibold text-white mb-1">GST / Website Required for AI Features</h2>
        <p className="text-sm text-gray-500 mb-4">
          Whether Get Details AI chat, flyer generation, and market research require GST number + company
          website to be filled in, per role. Independent of admin verification above — this only checks
          whether the two fields are present. Turning a toggle off lets that role use these features even
          with GST/website missing.
        </p>
        {gatesLoading ? sectionSpinner : (
        <>
        <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden max-w-2xl">
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800">
                <th className="text-left py-3 px-6 text-gray-500 font-medium">Feature</th>
                <th className="text-center py-3 px-6 text-gray-500 font-medium">Seller</th>
                <th className="text-center py-3 px-6 text-gray-500 font-medium">Buyer</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-gray-800/50">
                <td className="py-3 px-6 text-gray-300">GST / company website required</td>
                {(["seller", "buyer"] as GateRole[]).map((role) => {
                  const key = `gst_website_required.${role}`;
                  const enabled = gates?.gst_website_required?.[role] ?? true;
                  return (
                    <td key={role} className="py-3 px-6 text-center">
                      <button
                        onClick={() => toggleGstWebsiteGate(role)}
                        disabled={gateSavingKey === key}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 ${
                          enabled ? "bg-indigo-600" : "bg-gray-700"
                        }`}
                        aria-label={`${enabled ? "Disable" : "Enable"} GST/website requirement for ${role}`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            enabled ? "translate-x-6" : "translate-x-1"
                          }`}
                        />
                      </button>
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
          </div>
        </div>
        {gates?.updated_at && (
          <p className="text-xs text-gray-500 mt-2">Last updated: {(parseInstant(gates.updated_at) ?? new Date(gates.updated_at)).toLocaleString()}</p>
        )}
        </>
        )}
      </div>

      {/* Enquiry decline behavior — separate from Verification Gating above */}
      <div className="mt-8">
        <h2 className="text-lg font-semibold text-white mb-1">Enquiry on Decline (No/Close)</h2>
        <p className="text-sm text-gray-500 mb-4">
          Not a verification setting — this only decides what happens when a buyer clicks No / closes the
          deal AI chat confirm dialog. "Yes" is never affected by this toggle; it always goes through the
          Enquiries verification gate above instead.
        </p>
        {declineBehaviorLoading ? sectionSpinner : (
        <>
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 max-w-lg flex items-center justify-between">
          <span className="text-gray-300 text-sm">
            Declining {declineBehavior?.enabled ? "still silently sends the enquiry to the seller" : "sends nothing at all"}
          </span>
          <button
            onClick={toggleDeclineBehavior}
            disabled={declineBehaviorSaving || !declineBehavior}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 flex-shrink-0 ml-4 ${
              declineBehavior?.enabled ? "bg-indigo-600" : "bg-gray-700"
            }`}
            aria-label={`${declineBehavior?.enabled ? "Disable" : "Enable"} silent enquiry on decline`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                declineBehavior?.enabled ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
        </div>
        {declineBehavior?.updated_at && (
          <p className="text-xs text-gray-500 mt-2">
            Last updated: {(parseInstant(declineBehavior.updated_at) ?? new Date(declineBehavior.updated_at)).toLocaleString()}
          </p>
        )}
        </>
        )}
      </div>

      {/* GST Verification */}
      <div className="mt-8">
        <h2 className="text-lg font-semibold text-white mb-1">GST Verification</h2>
        <p className="text-sm text-gray-500 mb-4">
          When enabled, GSTIN values entered during onboarding/profile updates are verified against the
          GST portal. When disabled, GST numbers are saved without verification.
        </p>
        {gstLoading ? sectionSpinner : (
        <>
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 max-w-lg flex items-center justify-between">
          <span className="text-gray-300 text-sm">GST verification is {gstVerification?.enabled ? "started" : "stopped"}</span>
          <button
            onClick={toggleGstVerification}
            disabled={gstSaving || !gstVerification}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 ${
              gstVerification?.enabled ? "bg-indigo-600" : "bg-gray-700"
            }`}
            aria-label={`${gstVerification?.enabled ? "Disable" : "Enable"} GST verification`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                gstVerification?.enabled ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
        </div>
        {gstVerification?.updated_at && (
          <p className="text-xs text-gray-500 mt-2">
            Last updated: {(parseInstant(gstVerification.updated_at) ?? new Date(gstVerification.updated_at)).toLocaleString()}
          </p>
        )}
        </>
        )}
      </div>
      </>
      )}

      {activeTab === "pricing" && (
      <>
      <div>
        <h2 className="text-lg font-semibold text-white mb-1">Pricing</h2>
        <p className="text-sm text-gray-500 mb-4">
          Control which categories require sellers to enter pricing when creating a deal. Turning a
          toggle off makes pricing optional for that category.
        </p>
        {pricingLoading ? sectionSpinner : (
        <>
        {Object.entries(pricingSettings ?? {}).map(([groupSlug, categories]) => (
          <div key={groupSlug} className="mb-6">
            <h3 className="text-sm font-semibold text-gray-400 mb-2 capitalize">{groupSlug || "Ungrouped"}</h3>
            <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden max-w-2xl">
              <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-800">
                    <th className="text-left py-3 px-6 text-gray-500 font-medium">Category</th>
                    <th className="text-center py-3 px-6 text-gray-500 font-medium">Pricing required</th>
                  </tr>
                </thead>
                <tbody>
                  {categories.map((cat) => (
                    <tr key={cat.slug} className="border-b border-gray-800/50">
                      <td className="py-3 px-6 text-gray-300">{cat.name}</td>
                      <td className="py-3 px-6 text-center">
                        <button
                          onClick={() => togglePricingRequired(cat.slug, cat.pricing_required)}
                          disabled={pricingSavingSlug === cat.slug}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 ${
                            cat.pricing_required ? "bg-indigo-600" : "bg-gray-700"
                          }`}
                          aria-label={`${cat.pricing_required ? "Disable" : "Enable"} pricing requirement for ${cat.name}`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              cat.pricing_required ? "translate-x-6" : "translate-x-1"
                            }`}
                          />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          </div>
        ))}
        </>
        )}
      </div>
      </>
      )}

      {activeTab === "email" && (
      <>
      {/* Email Notifications */}
      <div>
        <h2 className="text-lg font-semibold text-white mb-1">Email Notifications</h2>
        <p className="text-sm text-gray-500 mb-4">
          Control which transactional emails the platform sends. Both default to on.
        </p>
        <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden max-w-2xl divide-y divide-gray-800">
          <div className="p-6 flex items-center justify-between">
            <div className="pr-4">
              <p className="text-gray-300 text-sm font-medium">Verification emails</p>
              <p className="text-xs text-gray-500 mt-1">
                Admin alert when a user completes onboarding, and the "you're verified" email sent to a
                buyer/seller once an admin approves them.
              </p>
              {verificationEmailLoading ? null : (
                <p className="text-xs text-gray-500 mt-1">
                  Currently {verificationEmail?.enabled ? "on" : "off"}
                  {verificationEmail?.updated_at && (
                    <> · Last updated: {(parseInstant(verificationEmail.updated_at) ?? new Date(verificationEmail.updated_at)).toLocaleString()}</>
                  )}
                </p>
              )}
            </div>
            {verificationEmailLoading ? sectionSpinner : (
              <button
                onClick={toggleVerificationEmail}
                disabled={verificationEmailSaving || !verificationEmail}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 flex-shrink-0 ${
                  verificationEmail?.enabled ? "bg-indigo-600" : "bg-gray-700"
                }`}
                aria-label={`${verificationEmail?.enabled ? "Disable" : "Enable"} verification emails`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    verificationEmail?.enabled ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            )}
          </div>
          <div className="p-6 flex items-center justify-between">
            <div className="pr-4">
              <p className="text-gray-300 text-sm font-medium">Transactional emails</p>
              <p className="text-xs text-gray-500 mt-1">
                Welcome, forgot-password, password-reset-confirmation, and profile email-verification
                emails. Leave this on — turning it off can lock users out of password reset or block
                them from verifying their email.
              </p>
              {transactionalEmailLoading ? null : (
                <p className="text-xs text-gray-500 mt-1">
                  Currently {transactionalEmail?.enabled ? "on" : "off"}
                  {transactionalEmail?.updated_at && (
                    <> · Last updated: {(parseInstant(transactionalEmail.updated_at) ?? new Date(transactionalEmail.updated_at)).toLocaleString()}</>
                  )}
                </p>
              )}
            </div>
            {transactionalEmailLoading ? sectionSpinner : (
              <button
                onClick={toggleTransactionalEmail}
                disabled={transactionalEmailSaving || !transactionalEmail}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 flex-shrink-0 ${
                  transactionalEmail?.enabled ? "bg-indigo-600" : "bg-gray-700"
                }`}
                aria-label={`${transactionalEmail?.enabled ? "Disable" : "Enable"} transactional emails`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    transactionalEmail?.enabled ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            )}
          </div>
          <div className="p-6 flex items-center justify-between">
            <div className="pr-4">
              <p className="text-gray-300 text-sm font-medium">Phone / WhatsApp OTP</p>
              <p className="text-xs text-gray-500 mt-1">
                Real WhatsApp verification codes for signup, login, OAuth new-account setup, and profile
                phone changes. Turning this off puts the platform into test mode: every phone OTP anywhere
                becomes the fixed code <code className="text-indigo-400">0000</code> and no real WhatsApp
                message is sent. Use only in non-production environments.
              </p>
              {phoneOtpLoading ? null : (
                <p className="text-xs text-gray-500 mt-1">
                  Currently {phoneOtp?.enabled ? "on" : "off (test mode — code is 0000)"}
                  {phoneOtp?.updated_at && (
                    <> · Last updated: {(parseInstant(phoneOtp.updated_at) ?? new Date(phoneOtp.updated_at)).toLocaleString()}</>
                  )}
                </p>
              )}
            </div>
            {phoneOtpLoading ? sectionSpinner : (
              <button
                onClick={togglePhoneOtp}
                disabled={phoneOtpSaving || !phoneOtp}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 flex-shrink-0 ${
                  phoneOtp?.enabled ? "bg-indigo-600" : "bg-gray-700"
                }`}
                aria-label={`${phoneOtp?.enabled ? "Disable" : "Enable"} phone/WhatsApp OTP`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    phoneOtp?.enabled ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            )}
          </div>
          <div className="p-6 flex items-center justify-between">
            <div className="pr-4">
              <p className="text-gray-300 text-sm font-medium">Delete unused uploads automatically</p>
              <p className="text-xs text-gray-500 mt-1">
                Files users attach in Flyers, Videos, Itinerary and Ask Deal but never use are removed from
                storage: every 10 minutes the server deletes files that have sat unused for 3 hours (Ask Deal
                files that were never sent, too). <strong>On:</strong> private documents such as ID scans do not
                pile up, and storage cost stays low. <strong>Off:</strong> nothing is deleted automatically, so
                unused files stay in storage until you switch it back on. Files already used in a generation or a
                sent message are never deleted, and no user sees any difference either way. Leaving a page
                still removes the files the user just dropped.
              </p>
              {uploadCleanupLoading ? null : (
                <p className="text-xs text-gray-500 mt-1">
                  Currently {uploadCleanup?.enabled ? "on" : "off (unused uploads are kept)"}
                  {uploadCleanup?.updated_at && (
                    <> · Last updated: {(parseInstant(uploadCleanup.updated_at) ?? new Date(uploadCleanup.updated_at)).toLocaleString()}</>
                  )}
                </p>
              )}
            </div>
            {uploadCleanupLoading ? sectionSpinner : (
              <button
                onClick={toggleUploadCleanup}
                disabled={uploadCleanupSaving || !uploadCleanup}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 flex-shrink-0 ${
                  uploadCleanup?.enabled ? "bg-indigo-600" : "bg-gray-700"
                }`}
                aria-label={`${uploadCleanup?.enabled ? "Disable" : "Enable"} automatic deletion of unused uploads`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    uploadCleanup?.enabled ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            )}
          </div>
          <div className="p-6 flex items-center justify-between">
            <div className="pr-4">
              <p className="text-gray-300 text-sm font-medium">AI reading of Ask Deal files</p>
              <p className="text-xs text-gray-500 mt-1">
                When a user attaches an itinerary, quote or ticket in Ask Deal, an AI model reads it (a paid call,
                limited per user per day). <strong>On:</strong> files are read as normal. <strong>Off:</strong> no file
                is read and no AI call is made; the user's message still goes through and they see "Reading files is
                switched off right now". Files already read stay in their chats; files not yet read are read normally
                once you switch it back on. Takes effect immediately.
              </p>
              {askFileReadingLoading ? null : (
                <p className="text-xs text-gray-500 mt-1">
                  Currently {askFileReading?.enabled ? "on" : "off (files are not read)"}
                  {askFileReading?.updated_at && (
                    <> · Last updated: {(parseInstant(askFileReading.updated_at) ?? new Date(askFileReading.updated_at)).toLocaleString()}</>
                  )}
                </p>
              )}
            </div>
            {askFileReadingLoading ? sectionSpinner : (
              <button
                onClick={toggleAskFileReading}
                disabled={askFileReadingSaving || !askFileReading}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 flex-shrink-0 ${
                  askFileReading?.enabled ? "bg-indigo-600" : "bg-gray-700"
                }`}
                aria-label={`${askFileReading?.enabled ? "Disable" : "Enable"} AI reading of Ask Deal files`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    askFileReading?.enabled ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            )}
          </div>
        </div>
      </div>
      </>
      )}

      {activeTab === "retention" && (
      <>
      {/* Data Retention */}
      <div>
        <h2 className="text-lg font-semibold text-white mb-4">Data Retention</h2>
        {retentionLoading ? sectionSpinner : (
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 space-y-4 max-w-lg">
          <div>
            <label className="block text-sm text-gray-400 mb-1">Chat sessions TTL (days)</label>
            <input
              type="number" min={0}
              value={retentionForm.session_days}
              onChange={e => setRetentionForm(f => ({ ...f, session_days: Number(e.target.value) }))}
              className="w-48 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white"
            />
            <p className="text-xs text-gray-500 mt-1">
              0 = never archive. Sessions older than this are moved to archive and removed from the main collection.
            </p>
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-1">Batch size (sessions per hourly sweep)</label>
            <input
              type="number" min={1} max={5000}
              value={retentionForm.batch_size}
              onChange={e => setRetentionForm(f => ({ ...f, batch_size: Number(e.target.value) }))}
              className="w-48 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white"
            />
            <p className="text-xs text-gray-500 mt-1">How many sessions to archive per hourly run. Increase to clear backlog faster.</p>
          </div>
          {retention?.updated_at && (
            <p className="text-xs text-gray-500">Last saved: {(parseInstant(retention.updated_at) ?? new Date(retention.updated_at)).toLocaleString()}{retention.updated_by ? ` by ${retention.updated_by}` : ""}</p>
          )}
          <button
            onClick={saveRetention}
            disabled={retentionSaving}
            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm disabled:opacity-50"
          >
            {retentionSaving ? "Saving..." : "Save Retention Settings"}
          </button>
        </div>
        )}
      </div>
      </>
      )}

      {activeTab === "s3backup" && (
      <>
      {/* S3 Backup */}
      <div>
        <h2 className="text-lg font-semibold text-white mb-1">S3 Backup</h2>
        <p className="text-sm text-gray-500 mb-4">
          New uploads are already backed up automatically and continuously via AWS S3 Replication
          (configured at the bucket level, outside this app — nothing to trigger for that). This button
          is a supplement: an on-demand sync you can run any time, e.g. right before a risky deploy, or to
          catch anything replication might have missed. It's incremental, so running it repeatedly only
          copies what's changed since the last run.
        </p>
        {s3BackupLoading ? sectionSpinner : (
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 max-w-2xl space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span
                className={`w-2.5 h-2.5 rounded-full ${
                  s3Backup?.state === "success" ? "bg-green-400" :
                  s3Backup?.state === "running" ? "bg-amber-400 animate-pulse" :
                  s3Backup?.state === "failed" ? "bg-red-400" : "bg-gray-600"
                }`}
              />
              <span className="text-white font-medium">
                {s3Backup?.state === "running" ? "Sync in progress…" :
                 s3Backup?.state === "success" ? "Last sync succeeded" :
                 s3Backup?.state === "failed" ? "Last sync failed" : "No sync run yet"}
              </span>
            </div>
            <button
              onClick={triggerS3Backup}
              disabled={s3BackupTriggering || s3Backup?.state === "running"}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm disabled:opacity-50"
            >
              {s3Backup?.state === "running" ? "Running…" : s3BackupTriggering ? "Starting…" : "Run Backup Now"}
            </button>
          </div>

          {(s3Backup?.state === "success" || s3Backup?.state === "failed") && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <div>
                <p className="text-gray-500">Scanned</p>
                <p className="text-white font-medium">{s3Backup.scanned ?? "—"}</p>
              </div>
              <div>
                <p className="text-gray-500">Copied</p>
                <p className="text-white font-medium">{s3Backup.copied ?? "—"}</p>
              </div>
              <div>
                <p className="text-gray-500">Skipped (unchanged)</p>
                <p className="text-white font-medium">{s3Backup.skipped ?? "—"}</p>
              </div>
              <div>
                <p className="text-gray-500">Errors</p>
                <p className={`font-medium ${s3Backup.errors ? "text-red-400" : "text-white"}`}>{s3Backup.errors ?? "—"}</p>
              </div>
            </div>
          )}

          {s3Backup?.error && (
            <p className="text-xs text-red-400 bg-red-500/10 rounded-lg p-3">{s3Backup.error}</p>
          )}
          {!!s3Backup?.error_samples?.length && (
            <div className="text-xs text-red-400 bg-red-500/10 rounded-lg p-3 space-y-1">
              {s3Backup.error_samples.map((e, i) => <p key={i}>{e}</p>)}
            </div>
          )}

          {(s3Backup?.started_at || s3Backup?.finished_at) && (
            <p className="text-xs text-gray-500">
              {s3Backup.started_at && <>Started: {(parseInstant(s3Backup.started_at) ?? new Date(s3Backup.started_at)).toLocaleString()}</>}
              {s3Backup.finished_at && <> · Finished: {(parseInstant(s3Backup.finished_at) ?? new Date(s3Backup.finished_at)).toLocaleString()}</>}
            </p>
          )}
        </div>
        )}
      </div>
      </>
      )}

      {activeTab === "inactivityNudge" && <InactivityNudgePanel />}

      {activeTab === "storage" && (
      <>
      <div>
        <h2 className="text-lg font-semibold text-white mb-1">Storage Backend Routing</h2>
        <p className="text-xs text-gray-500 mb-4 max-w-3xl">
          Each content type below (a "domain" — deals, webinars, etc.) can live in one of 3 places.{" "}
          <span className="font-semibold text-gray-300">Switching only decides where NEW uploads go from now on — it never moves or deletes anything that's already there.</span>
          {" "}
          <span className="text-gray-300">S3</span> and <span className="text-gray-300">Bunny Storage</span> are
          two separate storage lockers. Moving a domain's OLD files from one locker to the other needs an
          actual copy — that happens as a <span className="font-semibold text-gray-300">background migration job</span> (progress shown below each domain).
          Until that job finishes, old files just keep being read from whichever locker they're
          already sitting in — nothing breaks in the meantime, it's simply not instant.{" "}
          <span className="font-semibold text-gray-300">Migration never deletes the original copy</span> — once a file
          is copied, it briefly exists in both lockers until a separate cleanup step is run later.{" "}
          <span className="text-gray-300">S3 via Bunny cache</span> is different and needs no migration
          at all: files stay in S3, Bunny just sits in front as a faster delivery window — switching to
          it works instantly for every file, old and new (and switching away is just as instant).
          Several domains can migrate at the same time.
        </p>

        {/* Live circuit-breaker state of the three backends the read paths run on
            (GET /api/admin/storage-health). Amber, not red, when a breaker is
            open: it re-closes on its own after the cooldown, so "degraded" is
            the honest reading, not "down". */}
        <div className="flex items-center gap-4 mb-4 flex-wrap">
          {STORAGE_HEALTH_BACKENDS.map(({ key, label }) => {
            const state = storageHealth?.[key];
            const unknown = !state;
            const ok = state?.reachable === true;
            return (
              <span
                key={key}
                className="flex items-center gap-1.5 text-xs text-gray-400"
                title={
                  unknown
                    ? "Storage health unavailable"
                    : ok
                      ? `${label}: reachable`
                      : `${label}: degraded — retrying automatically${
                          state?.unhealthy_until != null ? " (cooling down)" : ""
                        }`
                }
              >
                <span
                  className={`w-2 h-2 rounded-full ${
                    unknown ? "bg-gray-600" : ok ? "bg-emerald-500" : "bg-amber-500"
                  }`}
                />
                {label}
              </span>
            );
          })}
        </div>

        {storageRoutingLoading ? sectionSpinner : !storageRouting ? (
          <p className="text-sm text-gray-500">Could not load storage routing config.</p>
        ) : (
        <>
        {storageDomainGroups.map(([group, domainsInGroup]) => (
          <details key={group} className="group mb-4" open>
            <summary className="cursor-pointer select-none flex items-center gap-2 text-sm font-semibold text-gray-300 mb-3">
              <span className="inline-block text-gray-600 transition-transform group-open:rotate-90">▸</span>
              {group}
              <span className="text-xs text-gray-600 font-normal">({domainsInGroup.length})</span>
            </summary>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 max-w-6xl mb-2">
              {domainsInGroup.map(([domain, backend]) => {
                const flippedAt = storageRouting.domain_updated_at[domain];
                const flippedAgoMs = flippedAt
                  ? Date.now() - (parseInstant(flippedAt) ?? new Date(flippedAt)).getTime()
                  : null;
                const job = migrations[domain];
                const jobActive = job?.status === "queued" || job?.status === "in_progress";
                // Only S3 and Bunny Storage have bytes that ever need moving —
                // "s3_via_bunny_cache" serves the same S3 bytes through the pull
                // zone with no copy at all (the backend rejects that mode with a
                // 400 either way), and it applies to every existing file the
                // instant the domain is flipped, so there's nothing to "catch up".
                const migrationDirection: "to_bunny_storage" | "to_s3" | null =
                  backend === "bunny_storage" ? "to_bunny_storage" : backend === "s3" ? "to_s3" : null;
                return (
                <div key={domain} className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="pr-4">
                      <p className="text-gray-300 text-sm font-medium">
                        {storageRouting.domain_labels[domain] || domain}
                      </p>
                      <p className="text-[11px] text-gray-600">{domain}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {storageRoutingSavingDomain === domain ? (
                        <span className="text-xs text-gray-500 px-3 py-1">Saving...</span>
                      ) : (
                        <>
                          {STORAGE_BACKEND_MODES.map(({ value, label }) => (
                            <button
                              key={value}
                              onClick={() => setStorageDomainBackend(domain, value)}
                              disabled={backend === value}
                              className={`px-3 py-1 rounded-lg text-xs border transition-colors ${
                                backend === value
                                  ? "bg-indigo-600 border-indigo-600 text-white"
                                  : "bg-gray-800 border-gray-700 text-gray-400 hover:text-white"
                              }`}
                            >
                              {label}
                            </button>
                          ))}
                        </>
                      )}
                    </div>
                  </div>

                  {(jobActive || (flippedAgoMs !== null && flippedAgoMs < 30 * 60 * 1000)) && (
                    <p className="mt-2 text-xs text-amber-500/80">
                      {jobActive
                        ? "Still migrating in the background — Verify below may show old files until this finishes."
                        : `Mode changed ${formatRelativeAgo(flippedAgoMs!)} ago — give it a few minutes before treating a Verify result as final.`}
                    </p>
                  )}

                  {/* Live-delivery proof: fetches one real key server-side through the
                      same read path a user's browser would hit, and shows exactly what
                      came back — the only way to see whether "S3 via Bunny cache" (whose
                      stored keys never change) is actually serving through Bunny. */}
                  <div className="mt-3 pt-3 border-t border-gray-800">
                    <div className="flex items-center gap-3 flex-wrap">
                      <button
                        onClick={() => runStorageDomainCacheCheck(domain)}
                        disabled={cacheChecking[domain] === true}
                        className="px-3 py-1 rounded-lg text-xs border border-gray-700 bg-gray-800 text-gray-300 hover:text-white disabled:opacity-50"
                      >
                        {cacheChecking[domain] ? "Checking..." : "Verify live delivery"}
                      </button>
                      <span className="text-xs text-gray-600">
                        Fetches one real file for this domain the same way a visitor's browser
                        would, and shows exactly where it came from and whether it loaded.
                      </span>
                    </div>
                    {cacheCheck[domain] && (() => {
                      const result = cacheCheck[domain];
                      if (!result.sample_key) {
                        return (
                          <p className="mt-2 text-xs text-gray-500">
                            {result.error || result.message || "No stored file found to test against."}
                          </p>
                        );
                      }
                      const resolvedLabel = backendLabel(result.mode);
                      const mismatchNote =
                        result.mode && result.mode !== backend
                          ? explainStorageModeMismatch(backend, result.mode)
                          : null;
                      return (
                        <div
                          className={`mt-2 rounded-lg border p-3 text-xs ${
                            result.reachable
                              ? "border-emerald-900 bg-emerald-950/40"
                              : "border-red-900 bg-red-950/40"
                          }`}
                        >
                          <p className={result.reachable ? "text-emerald-400" : "text-red-400"}>
                            {result.reachable
                              ? `Confirmed — this file is actually being served through ${resolvedLabel} (${result.resolved_host}, HTTP ${result.status_code}).`
                              : `Not reaching the file this way — resolved to ${resolvedLabel} at ${result.resolved_host}${
                                  result.status_code ? ` (HTTP ${result.status_code})` : ""
                                }${result.error ? `: ${result.error}` : ""}.`}
                          </p>
                          {mismatchNote && (
                            <p className="mt-1 text-amber-500/90">{mismatchNote}</p>
                          )}
                          <p className="mt-1 text-gray-600 break-all">
                            Tested key: <code className="text-gray-500">{result.sample_key}</code>
                          </p>
                        </div>
                      );
                    })()}
                  </div>

                  {migrationDirection && (() => {
                    const toBunny = migrationDirection === "to_bunny_storage";
                    return (
                    <div className="mt-3 pt-3 border-t border-gray-800">
                      <div className="flex items-center gap-3 flex-wrap">
                        <button
                          onClick={() => startStorageDomainMigration(domain)}
                          disabled={migrationStarting[domain] === true || jobActive}
                          className="px-3 py-1 rounded-lg text-xs border border-gray-700 bg-gray-800 text-gray-300 hover:text-white disabled:opacity-50"
                        >
                          {migrationStarting[domain]
                            ? "Queueing..."
                            : job?.status === "queued"
                              ? "Queued — waiting for a worker"
                              : job?.status === "in_progress"
                                ? "Migrating in the background..."
                                : job
                                  ? "Re-run migration"
                                  : "Migrate existing objects"}
                        </button>
                        <span className="text-xs text-gray-600">
                          {toBunny
                            ? "Runs automatically when you switch a domain to Bunny Storage; this button is for re-running it. Copies objects still in S3 to the same path on Bunny Storage, skipping any already there, then tags their keys."
                            : "Runs automatically when you switch a domain back to S3; this button is for re-running it. Copies objects still tagged Bunny Storage back into S3, skipping any already there, then untags their keys."}
                          {" "}Runs on the server, so you can leave this page. Never deletes the {toBunny ? "S3" : "Bunny"} original.
                        </span>
                      </div>

                      {migrationFailure[domain] && (
                        <p className="mt-2 text-xs text-red-400">{migrationFailure[domain]}</p>
                      )}

                      {job && (
                        <details className="group/job mt-3 bg-gray-950 border border-gray-800 rounded-lg p-3" open={jobActive}>
                          <summary className="cursor-pointer select-none text-xs text-gray-400 flex items-center gap-2">
                            <span className="inline-block text-gray-600 transition-transform group-open/job:rotate-90">▸</span>
                            {job.status === "completed"
                              ? `Finished — ${job.progress.rewritten_documents} rewritten, ${job.progress.errors_total} skipped`
                              : job.status === "failed"
                                ? "Failed — see details"
                                : jobActive
                                  ? `In progress — ${job.progress.scanned_documents} documents scanned so far`
                                  : "Details"}
                          </summary>
                          <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-y-1 gap-x-4 text-xs">
                            <span className="text-gray-500">Documents scanned</span>
                            <span className="text-gray-300 sm:col-span-2">{job.progress.scanned_documents}</span>
                            <span className="text-gray-500">Distinct keys</span>
                            <span className="text-gray-300 sm:col-span-2">{job.progress.scanned_keys}</span>
                            <span className="text-gray-500">Already at target</span>
                            <span className="text-gray-300 sm:col-span-2">{job.progress.already_present_at_target}</span>
                            <span className="text-gray-500">Copied</span>
                            <span className="text-gray-300 sm:col-span-2">{job.progress.copied}</span>
                            <span className="text-gray-500">Documents rewritten</span>
                            <span className="text-gray-300 sm:col-span-2">{job.progress.rewritten_documents}</span>
                          </div>
                          {job.error && (
                            <p className="mt-2 text-xs text-red-400">{job.error}</p>
                          )}
                          {job.progress.errors.length > 0 && (
                            <div className="mt-2">
                              <p className="text-xs text-amber-400 mb-1">
                                {job.progress.errors_total} key(s) skipped — still served from the source
                                backend, safe to re-run
                                {job.progress.errors_total > job.progress.errors.length
                                  ? ` (first ${job.progress.errors.length} shown)`
                                  : ""}:
                              </p>
                              <ul className="max-h-40 overflow-y-auto space-y-1">
                                {job.progress.errors.map((e, i) => (
                                  <li key={`${e.key}-${e.stage}-${i}`} className="text-xs text-gray-500 break-all">
                                    <span className="text-gray-400">[{e.stage}]</span> {e.key} — {e.error}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {jobActive && (
                            <p className="mt-2 text-xs text-gray-600">
                              Updating every {MIGRATION_POLL_MS / 1000}s. Other domains you queue run
                              alongside this one.
                            </p>
                          )}
                        </details>
                      )}
                    </div>
                    );
                  })()}
                </div>
                );
              })}
            </div>
          </details>
        ))}
        {storageRouting.updated_at && (
          <p className="text-xs text-gray-500 mt-3">
            Last changed: {(parseInstant(storageRouting.updated_at) ?? new Date(storageRouting.updated_at)).toLocaleString()}
            {storageRouting.updated_by ? ` by ${storageRouting.updated_by}` : ""}
          </p>
        )}
        </>
        )}
      </div>

      {/* Key-path inventory: per-user S3/Bunny key paths for the selected domain.
          Live scan of that domain's collection, not a maintained index — see
          backend/app/modules/admin/storage_inventory_routes.py. */}
      <div className="mt-8">
        <h2 className="text-lg font-semibold text-white mb-1">Stored Key Paths (per user)</h2>
        <p className="text-xs text-gray-500 mb-4 max-w-4xl">
          Live scan of the selected module's documents — shows every S3/Bunny key found, grouped by
          owning user. Scans up to {INVENTORY_PAGE_SIZE} documents per collection (a domain like "deals" covers
          the legacy collection plus all 7 typed per-category collections). "scanned / total" below shows
          how much of each collection this page covered. Because it reads what is stored right now,
          flipping a domain's mode does not change these keys: on{" "}
          <span className="text-gray-300">Bunny Storage</span> they keep showing the old backend
          until that domain's migration job has processed them, and on{" "}
          <span className="text-gray-300">S3 via Bunny cache</span> they never change at all — those
          objects stay in S3 on purpose and the read path serves them through the cache live.
        </p>
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <select
            value={inventoryDomain}
            onChange={(e) => setInventoryDomain(e.target.value)}
            className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white"
          >
            {storageRouting && Object.keys(storageRouting.domains).map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
          <select
            value={inventorySellerName}
            onChange={(e) => {
              const name = e.target.value;
              setInventorySellerName(name);
              setInventorySkip(0);
              loadInventory(inventoryDomain, 0, name);
            }}
            className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white w-64"
          >
            <option value="">
              All {(inventory?.owner_label || "users").toLowerCase()}s ({inventoryOwners.length})
            </option>
            {inventoryOwners.map((o) => (
              // Prefer email as the filter value when available — unique, whereas
              // two sellers can share the same display name. Backend regex-matches
              // this value against name/seller_name/agency_name/email either way.
              <option key={o.id} value={o.email || o.name}>
                {o.email ? `${o.name} (${o.email})` : o.name}
              </option>
            ))}
          </select>
          <input
            type="text"
            value={inventorySellerName}
            onChange={(e) => setInventorySellerName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                setInventorySkip(0);
                loadInventory(inventoryDomain, 0, inventorySellerName);
              }
            }}
            placeholder="...or type name/email to search"
            className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white w-56"
          />
          <button
            onClick={() => { setInventorySkip(0); loadInventory(inventoryDomain, 0, inventorySellerName); }}
            disabled={inventoryLoading}
            className="px-3 py-2 rounded-lg text-sm bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-50"
          >
            {inventoryLoading ? "Syncing..." : "Sync from live DB"}
          </button>
        </div>
        {inventoryLastSynced && (
          <p className="text-xs text-gray-600 mb-4">Last synced: {inventoryLastSynced.toLocaleTimeString()}</p>
        )}

        {inventoryLoading ? sectionSpinner : !inventory ? (
          <p className="text-sm text-gray-500">Could not load inventory for this domain.</p>
        ) : inventory.error ? (
          <p className="text-sm text-gray-500">{inventory.error}</p>
        ) : (
        <div className="bg-gray-900 border border-gray-800 rounded-xl max-w-6xl overflow-hidden">
          <div className="px-4 py-2 text-xs text-gray-500 border-b border-gray-800">
            Collections: <code className="text-gray-400">{inventory.collections.join(", ")}</code>
            <span className="mx-1">·</span>
            Page {Math.floor(inventory.skip / INVENTORY_PAGE_SIZE) + 1} of {Math.max(1, Math.ceil(inventory.total_documents_in_collections / INVENTORY_PAGE_SIZE))}
            <span className="mx-1">·</span>
            page size {INVENTORY_PAGE_SIZE}/collection
            <span className="mx-1">·</span>
            scanned {inventory.scanned_documents} / {inventory.total_documents_in_collections} total documents this page
            {inventory.backend_counts && (
              <>
                <span className="mx-1">·</span>
                <span className="px-1.5 py-0.5 rounded bg-blue-900 text-blue-300">S3 {inventory.backend_counts.s3}</span>
                {" "}
                <span className="px-1.5 py-0.5 rounded bg-purple-900 text-purple-300">Bunny {inventory.backend_counts.bunny}</span>
              </>
            )}
          </div>
          {inventory.users.length === 0 ? (
            <p className="text-sm text-gray-500 p-4">No keys found in this scan window.</p>
          ) : (
          <div className="divide-y divide-gray-800">
            {inventory.users.map((u) => (
              <div key={u.owner_id} className="p-4">
                <p className="text-sm text-gray-300 font-medium mb-2">
                  {inventory.owner_label || "User"}: {u.owner_name || "Unknown"}{" "}
                  <code className="text-indigo-400 text-xs">{u.owner_id}</code>
                </p>
                {u.documents.map((doc) => (
                  <div key={doc.collection + doc.document_id} className="mb-2 last:mb-0">
                    <p className="text-[11px] text-gray-600 mb-1">
                      <code>{doc.collection}</code> · doc {doc.document_id}
                    </p>
                    <ul className="space-y-1">
                      {doc.items.map((item, i) => (
                        <li key={i} className="text-xs font-mono text-gray-400 flex items-center gap-2">
                          <span className={`px-1.5 py-0.5 rounded ${item.backend === "bunny" ? "bg-purple-900 text-purple-300" : "bg-blue-900 text-blue-300"}`}>
                            {item.backend}
                          </span>
                          <span className="text-gray-600">{item.field_path}:</span>
                          {item.url ? (
                            <a href={item.url} target="_blank" rel="noreferrer" className="truncate text-indigo-400 hover:underline">{item.key}</a>
                          ) : (
                            <span className="truncate">{item.key}</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            ))}
          </div>
          )}
          <div className="p-4 flex items-center gap-2 border-t border-gray-800">
            <button
              onClick={() => { const s = Math.max(0, inventorySkip - INVENTORY_PAGE_SIZE); setInventorySkip(s); loadInventory(inventoryDomain, s, inventorySellerName); }}
              disabled={inventorySkip === 0}
              className="px-3 py-1 rounded-lg text-xs bg-gray-800 border border-gray-700 text-gray-400 hover:text-white disabled:opacity-50"
            >
              ← Previous page
            </button>
            <span className="text-xs text-gray-600">
              Page {Math.floor(inventorySkip / INVENTORY_PAGE_SIZE) + 1}
            </span>
            <button
              onClick={() => { const s = inventorySkip + INVENTORY_PAGE_SIZE; setInventorySkip(s); loadInventory(inventoryDomain, s, inventorySellerName); }}
              disabled={inventorySkip + INVENTORY_PAGE_SIZE >= inventory.total_documents_in_collections}
              className="px-3 py-1 rounded-lg text-xs bg-gray-800 border border-gray-700 text-gray-400 hover:text-white disabled:opacity-50"
            >
              Next page →
            </button>
          </div>
        </div>
        )}
      </div>
      </>
      )}

      {activeTab === "websocket" && <AdminWebSocketHealthPage />}
      {activeTab === "recRedisHealth" && <AdminRecRedisHealthPage />}
      {activeTab === "cdnRedisHealth" && <AdminCdnRedisHealthPage />}
    </div>
  );
};

export default AdminSettingsPage;
